import { useCallback, useEffect, useRef, useState } from 'react';

import {
  type SuperlikeAnimationPhase,
  superlikeAnimationSchedule,
  type SuperlikeScheduleOptions,
} from './superlikeAnimation';

export type PlaySuperlikeAnimationParams = SuperlikeScheduleOptions & {
  /**
   * Runs when the sequence ends, or at once if the component unmounts first,
   * so the send's effects (the card's removal, the counters) always land.
   */
  onDone: () => void;
};

/**
 * The send animation's state machine: `play` walks the schedule's phases
 * on timers and returns to 'idle' when it is done. `isPlayingRef` is for
 * handlers kept by older renders (the tab bar's buttons).
 */
export const useSuperlikeSendAnimation = () => {
  const [phase, setPhase] = useState<SuperlikeAnimationPhase>('idle');
  const isPlayingRef = useRef(false);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const pendingDoneRef = useRef<(() => void) | null>(null);

  const finish = useCallback(() => {
    const onDone = pendingDoneRef.current;

    pendingDoneRef.current = null;
    isPlayingRef.current = false;
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
    setPhase('idle');
    onDone?.();
  }, []);

  const play = useCallback(
    ({ onDone, ...options }: PlaySuperlikeAnimationParams) => {
      if (isPlayingRef.current) return;

      const [first, ...rest] = superlikeAnimationSchedule(options);

      isPlayingRef.current = true;
      pendingDoneRef.current = onDone;
      setPhase(first.phase === 'done' ? 'idle' : first.phase);

      timersRef.current = rest.map(({ phase: next, atMs }) =>
        setTimeout(() => {
          if (next === 'done') {
            finish();

            return;
          }

          setPhase(next);
        }, atMs),
      );
    },
    [finish],
  );

  useEffect(
    () => () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];

      if (pendingDoneRef.current) {
        const onDone = pendingDoneRef.current;

        pendingDoneRef.current = null;
        isPlayingRef.current = false;
        onDone();
      }
    },
    [],
  );

  return {
    phase,
    isPlaying: phase !== 'idle',
    isPlayingRef,
    play,
  };
};
